/**
 * La posición del elemento resaltado, cuadro a cuadro.
 *
 * Se mide con `requestAnimationFrame` en vez de escuchar `scroll` y
 * `resize`: el propio motor hace `scrollIntoView`, la barra lateral se
 * colapsa con muelle y varias tarjetas entran animadas con framer-motion.
 * Con listeners, el recorte del foco se queda atrás durante la animación y
 * se ve el borde persiguiendo al botón.
 *
 * Sólo corre mientras hay un elemento resaltado, y sólo re-renderiza cuando
 * el rectángulo cambia de verdad. La medida se guarda **junto al elemento
 * que la produjo**: así, al cambiar de paso, el rectángulo viejo deja de
 * valer en el mismo render, sin tener que limpiarlo desde un efecto.
 */

import { useEffect, useState } from 'react';

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface Measurement {
  element: HTMLElement | null;
  rect: TargetRect | null;
}

const sameRect = (a: TargetRect | null, b: TargetRect | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
};

export function useTargetRect(element: HTMLElement | null): TargetRect | null {
  const [measurement, setMeasurement] = useState<Measurement>({
    element: null,
    rect: null,
  });

  useEffect(() => {
    if (!element) return;

    let frame = 0;
    let current: TargetRect | null = null;

    const measure = () => {
      const box = element.getBoundingClientRect();
      const next: TargetRect = {
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
      };
      if (!sameRect(current, next)) {
        current = next;
        setMeasurement({ element, rect: next });
      }
      frame = requestAnimationFrame(measure);
    };

    frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [element]);

  return measurement.element === element ? measurement.rect : null;
}
